import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { Coins, SquareArrowOutUpRight } from 'lucide-react'
import { formatTokenAmount } from '@/lib/format'
import { Modal } from './modal'
import { AddressField } from './address-field'
import { CORE_TOKEN_LOGOS, tokenLogo } from '@/lib/default-tokens'
import { COIN_NETWORKS, EXCHANGE_COIN_IDS, exchangeCoinOf, NETWORK_LABELS, networkOfPlace, networkPlace, txExplorerUrl, type ExchangeCoin, type Network } from '@/lib/networks'
import type { PickedToken } from '@/lib/picked-token'
import { TokenIcon } from './holdings-table'
import { AssetChip } from './asset-chips'
import { Spinner } from './spinner'
import { FlowProgress } from './flow-progress'
import { errorMessage, useTRPC } from '@/lib/trpc'
import type { MeWallet, PrivateDepositView, VaultDepositView } from '@/lib/api-types'
import { useCustody, usePoolMode, CUSTODY_LABELS, type ExchangeId } from '@/lib/pool'
import { useSetCustody } from '@/lib/custody'
import { PlatformGroup, usePlatformOptions, type ChipOption } from './platform-chip'
import { HeaderRule } from './header-chips'
import { isExchangeId } from '@/lib/pool'
import { countdownText, useCountdown } from '@/lib/eta'
import { markPortfolioFresh, useInvalidatePortfolio } from '@/lib/portfolio'

const STEPS = ['Funding', 'Routing', 'Depositing', 'Complete'] as const
const TOKEN_STEPS = ['Funding', 'Routing', 'Depositing', 'Complete'] as const
const TERMINAL = new Set(['completed', 'failed', 'stalled'])

function stepIndex(status: PrivateDepositView['status']) {
  switch (status) {
    case 'awaitingFunds':
      return 0
    case 'funded':
    case 'sweepSent':
      return 1
    case 'relayPending':
    case 'stalled':
      return 2
    case 'completed':
      return 3
    default:
      return -1
  }
}

/** raw units of the deposit's asset, formatted with its symbol */
function amountOf(raw: string | null | undefined, asset: PrivateDepositView['asset']) {
  if (!raw) return ''
  return `${formatTokenAmount(Number(raw) / 10 ** asset.decimals, asset.symbol, 6)} ${asset.symbol}`
}
/** the number alone, for lines that show the token as a chip */
function numberOf(raw: string | null | undefined, asset: PrivateDepositView['asset']) {
  if (!raw) return ''
  return formatTokenAmount(Number(raw) / 10 ** asset.decimals, asset.symbol, 6)
}

/**
 * One address for everything on Solana, no toggles (John, Oct 29 2026):
 * SOL, USDC and USDT arrive privately through Relay; any other token sent
 * to the same address is handed to the vault.
 */
export function DepositModal({ open, onClose, wallet, initialId = null, initialToken = null }: { open: boolean; onClose: () => void; wallet: MeWallet; initialId?: string | null; /** the row the dialog was opened from (John, Nov 4 2026): its coin, on its own chain */ initialToken?: PickedToken | null }) {
  const [footer, setFooter] = useState<React.ReactNode>(undefined)
  // the QR code folds open under the address from a text link at the bottom (John, Nov 4 2026: no header icon); a new
  // address (or none on screen) folds it away again
  const [showQr, setShowQr] = useState(false)
  const setAddress = useCallback((_address: string | null) => setShowQr(false), [])
  const qr: QrControl = { show: showQr, toggle: () => setShowQr((v) => !v), setAddress }
  // the chains, after the title: the pool deposit puts its network chips here while an address waits (John, Nov 4 2026: "5 network chip icon buttons on the topnav next to Deposit"; Nov 2: "an angle bar and then..." the group)
  const [picker, setPicker] = useState<React.ReactNode>(undefined)
  const poolMode = usePoolMode()
  return (
    <Modal
      open={open}
      onClose={onClose}
      // one size wider than the other dialogs: a full Solana address then fits on one line at the normal size (John, Oct 31 2026)
      width="lg"
      footer={footer}
      title={
        <span className="flex items-center gap-3">
          Deposit
          {picker ? (
            <>
              <HeaderRule />
              {picker}
            </>
          ) : null}
        </span>
      }
    >
      {poolMode ? (
        <PoolDeposit wallet={wallet} onClose={onClose} setFooter={setFooter} setPicker={setPicker} initialId={initialId} initialToken={initialToken} qr={qr} />
      ) : (
        <PrivateDeposit wallet={wallet} onClose={onClose} setFooter={setFooter} qr={qr} />
      )}
    </Modal>
  )
}

/** the QR code: whether it is open, the link that flips it, and how an address card folds it away when its address changes */
type QrControl = { show: boolean; toggle: () => void; setAddress: (address: string | null) => void }

const POOL_STEPS = ['Funding', 'Depositing', 'Complete'] as const
/** what the dialog is set to: a Solana coin, any Solana token (the vault's one address), or the exchanges' ETH or BNB (Nov 3 2026) */
type DepositAsset = 'SOL' | 'USDC' | 'USDT' | 'ETH' | 'BNB' | 'token'
const POOL_TERMINAL = new Set(['credited', 'failed', 'cancelled'])

function poolStep(status: VaultDepositView['status']) {
  switch (status) {
    case 'awaitingFunds':
      return 0
    case 'processing':
      return 1
    case 'credited':
      return 2
    default:
      return -1
  }
}

/** True once `active` has held for `ms`: a loader that would only flash stays hidden. */
function useHeld(active: boolean, ms: number) {
  const [held, setHeld] = useState(false)
  useEffect(() => {
    if (!active) return
    const timer = window.setTimeout(() => setHeld(true), ms)
    return () => {
      window.clearTimeout(timer)
      setHeld(false)
    }
  }, [active, ms])
  return held
}

/** the Token chips: the exchange coins, and any Solana token for the vault's one address (John, Nov 4 2026: "an arbitrary SOL Token option... for my own sanity") */
type DepositToken = ExchangeCoin | 'token'
const DEPOSIT_TOKENS: readonly DepositToken[] = ['SOL', 'ETH', 'BNB', 'USDC', 'USDT', 'token']
/** the Network chips in the header, in the chains' order */
const DEPOSIT_NETWORKS: readonly Network[] = ['solana', 'ethereum', 'base', 'bsc', 'robinhood']
/** the chains a token comes in on; any Solana token on Solana alone */
const networksOf = (token: DepositToken): readonly Network[] => (token === 'token' ? ['solana'] : COIN_NETWORKS[token])
/** the first token a chain carries, for when the picked one is not on it */
const firstTokenOn = (network: Network): DepositToken => DEPOSIT_TOKENS.find((token) => networksOf(token).includes(network)) ?? 'SOL'
const tokenLabel = (token: DepositToken) => (token === 'token' ? 'Any Solana token' : token)
/** where a deposit lands: the Dispence vault (an arbitrary token stays there) or one of the exchanges */
type DepositPlace = 'vault' | ExchangeId
const placeLabel = (place: DepositPlace) => (place === 'vault' ? 'Dispence vault' : CUSTODY_LABELS[place])

/**
 * Pool custody (John, Nov 4 2026): the chain is picked in the header, the
 * token on the left, the place on the right; a place that does not take the
 * token on the chain is greyed out, and the pick moves to the first that does.
 * The vault's one address takes any Solana token (and forwards SOL, USDC and
 * USDT on to the exchange); an exchange's own address takes a coin on a chain.
 */
function PoolDeposit({ wallet, onClose, setFooter, setPicker, initialId = null, initialToken = null, qr }: { wallet: MeWallet; onClose: () => void; setFooter: (footer: React.ReactNode) => void; initialToken?: PickedToken | null; /** the header's network chips, there while the address waits */ setPicker: (picker: React.ReactNode) => void; initialId?: string | null; qr: QrControl }) {
  const trpc = useTRPC()
  const { exchange, dispenceVault, custody } = useCustody()
  // where the deposit lands (John, Nov 1 2026): the pick names the platform and the account remembers it
  const platformOptions = usePlatformOptions()
  const { set: setCustody } = useSetCustody()
  const queryClient = useQueryClient()
  const invalidatePortfolio = useInvalidatePortfolio()
  // opened on a deposit in progress (the activity bar's View): that one, not the waiting address
  const [activeId, setActiveId] = useState<string | null>(initialId)
  const [error, setError] = useState<string | null>(null)
  // an exchange: one vault address takes anything and routes SOL, USDC and USDT on to the exchange (John, Oct 31 2026);
  // without a vault, the exchange's own per-asset addresses. The link between the two is gone (John, Nov 1 2026: redundant).
  const direct = !dispenceVault
  // opened from a row (John, Nov 4 2026: "if i click eg BNB then deposit, it defaults onto solana which doesnt make sense"):
  // that coin, on the row's chain or the coin's own first one (ETH Ethereum, BNB BSC, the stables Solana); any other
  // Solana token goes to the vault's address; the header's Deposit starts on SOL. The place then moves to the first
  // exchange that takes the coin on that chain (the effect below), the remembered one when it does.
  const openedCoin = exchangeCoinOf(initialToken)
  const openedToken: DepositToken = openedCoin ?? (initialToken && dispenceVault ? 'token' : 'SOL')
  const [network, setNetwork] = useState<Network>(() => {
    const chains = networksOf(openedToken)
    return initialToken?.network && chains.includes(initialToken.network) ? initialToken.network : chains[0]
  })
  const [token, setToken] = useState<DepositToken>(openedToken)
  // the Dispence vault as the place, for an arbitrary token; an exchange otherwise, the account's remembered one
  const [toVault, setToVault] = useState(openedToken === 'token')
  // the exchange a deposit opened from the bar's View is at, when that is not the account's remembered one: shown as the
  // place without changing the remembered one (Nov 5 2026); a pick of a place clears it
  const [viewExchange, setViewExchange] = useState<ExchangeId | null>(null)
  const place: DepositPlace = toVault && dispenceVault ? 'vault' : (viewExchange ?? custody)
  // which chains each platform takes each coin on, read for every platform ahead (John, Nov 4 2026: a place that does not
  // take the pick is greyed out and unclickable, and the pick moves to the first that does)
  const RAIL_COINS = ['SOL', 'USDC', 'USDT', 'ETH', 'BNB'] as const
  const coinRails = useQueries({
    queries: platformOptions.flatMap((option) =>
      RAIL_COINS.map((symbol) => ({ ...trpc.vault.networks.queryOptions({ platform: option.id as ExchangeId, symbol, rail: 'deposit' as const }), enabled: exchange, staleTime: 5 * 60_000 })),
    ),
  })
  const railAt = (platform: string, symbol: ExchangeCoin) => {
    const i = platformOptions.findIndex((option) => option.id === platform)
    return i < 0 ? undefined : coinRails[i * RAIL_COINS.length + RAIL_COINS.indexOf(symbol)]
  }
  /** whether a place takes a token on a chain: the vault any Solana token and nothing else; an exchange what its rails say */
  const takes = (at: string, want: DepositToken, on: Network) => {
    if (want === 'token') return at === 'vault' && dispenceVault && on === 'solana'
    if (at === 'vault') return false
    return (railAt(at, want)?.data ?? []).includes(on)
  }
  /** the place's rails for the token have been read (or the read failed, which counts as closed) */
  const known = (at: string, want: DepositToken) => {
    if (want === 'token' || at === 'vault') return true
    const rail = railAt(at, want)
    return rail?.isSuccess === true || rail?.isError === true
  }
  const places: DepositPlace[] = ['vault', ...platformOptions.map((option) => option.id as ExchangeId)]
  const placeOptions: ChipOption[] = places.map((at) => ({ id: at, label: placeLabel(at), disabled: known(at, token) && !takes(at, token, network) }))
  const tokenOptions: ChipOption[] = DEPOSIT_TOKENS.map((id) => ({
    id,
    label: tokenLabel(id),
    mark: id === 'token' ? <Coins className="size-5" aria-hidden="true" /> : <TokenIcon symbol={id} imageUrl={CORE_TOKEN_LOGOS[EXCHANGE_COIN_IDS[id]]} size="size-6" />,
    disabled: id === 'token' && !dispenceVault,
  }))
  /** a new pick: the last card stays on screen, dimmed, until the next address is in; a failed request of the last pick is forgotten with it */
  const createRef = useRef<{ reset: () => void } | null>(null)
  const restart = () => {
    setSwitching(true)
    setActiveId(null)
    setError(null)
    createRef.current?.reset()
  }
  // the token follows the chain and the chain the token, so the two never disagree
  const pickNetwork = (id: string) => {
    const next = networkOfPlace(id)
    if (!next || next === network) return
    restart()
    setNetwork(next)
    if (!networksOf(token).includes(next)) setToken(firstTokenOn(next))
  }
  const pickToken = (id: string) => {
    const next = id as DepositToken
    if (next === token) return
    restart()
    setToken(next)
    if (!networksOf(next).includes(network)) setNetwork(networksOf(next)[0])
  }
  const pickPlace = (id: string) => {
    if (id === place) return
    restart()
    // the pick shows at once, ahead of the remembered place catching up (a window of the old place would otherwise be read)
    setViewExchange(isExchangeId(id) ? id : null)
    if (id === 'vault') setToVault(true)
    else {
      setToVault(false)
      if (id !== custody) setCustody.mutate({ custody: id as ExchangeId })
    }
  }
  // the place does not take the token on the chain: the first that does, the vault for an arbitrary token, else the
  // exchanges in their order (John, Nov 4 2026: "autoswitch the CEX to the first available one", Coinbase, MEXC, KuCoin,
  // Binance.US). The pick stays where no place takes it, with no address to show.
  useEffect(() => {
    if (!exchange || !known(place, token) || takes(place, token, network)) return
    // every place's answer first, so the first by priority wins rather than the first to answer
    if (!platformOptions.every((option) => known(option.id, token))) return
    const next = places.find((at) => takes(at, token, network))
    if (!next) return
    restart()
    setViewExchange(next === 'vault' ? null : next)
    if (next === 'vault') setToVault(true)
    else {
      setToVault(false)
      if (next !== custody) setCustody.mutate({ custody: next })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place, token, network, coinRails.map((q) => `${q.isSuccess || q.isError}:${q.data?.join('/') ?? '-'}`).join()])
  // what the window is opened for: any token, and a Solana coin where there is a vault, go to the vault's one address;
  // an exchange's own address otherwise, on the chain picked
  const asset: DepositAsset = token === 'token' || (network === 'solana' && dispenceVault) ? 'token' : token
  const windowNetwork = network === 'solana' ? undefined : network
  const unsupported = known(place, token) && !takes(place, token, network)
  const ready = known(place, token) && !unsupported
  const windowInput = { walletId: wallet.id, asset, platform: place === 'vault' ? custody : place, ...(windowNetwork ? { network: windowNetwork } : {}) }
  // every other platform's waiting address is read ahead, so a switch shows it at once (John, Nov 1 2026: "pre-warmed")
  useQueries({
    queries: platformOptions.flatMap((option) =>
      isExchangeId(option.id) && option.id !== custody
        ? [{ ...trpc.vault.openPoolDeposit.queryOptions({ walletId: wallet.id, asset: dispenceVault ? ('token' as const) : ('SOL' as const), platform: option.id }), staleTime: 30_000 }]
        : [],
    ),
  })
  // the address still waiting from last time; the dashboard reads it ahead so the dialog opens with it on screen
  const openKey = trpc.vault.openPoolDeposit.queryKey(windowInput)
  const existing = useQuery({ ...trpc.vault.openPoolDeposit.queryOptions(windowInput), staleTime: 30_000, enabled: ready })
  /**
   * The deposit is the pick's own: on its chain, and its coin at its place, or the vault's any-token address (Nov 5 2026:
   * an address asked for under one pick came back after the pick had changed, and a USDC-on-BSC address sat under
   * "SOL on Solana" with the exchanges greyed out around it)
   */
  const fits = (d: VaultDepositView) => {
    // money already in it is the deposit being watched, whatever the pickers say: a coin bound for an exchange through the
    // vault's one address turns from the vault's any-asset window into the exchange's coin the moment it lands (Nov 5 2026:
    // two SOL deposits to Coinbase went through and the dialog swapped to a fresh address instead of Depositing and Complete)
    if (d.status !== 'awaitingFunds') return true
    if (d.network !== network) return false
    if (asset === 'token') return d.platform === 'vault' || d.anyAsset === true
    return d.tokenSymbol === token && d.platform === place
  }
  const windowInputRef = useRef(windowInput)
  windowInputRef.current = windowInput
  /** shows a deposit at once instead of waiting for its own query to come back */
  function show(deposit: VaultDepositView, key: readonly unknown[] = openKey) {
    queryClient.setQueryData(trpc.vault.deposit.queryKey({ id: deposit.id }), deposit)
    queryClient.setQueryData(key, deposit)
    setActiveId(deposit.id)
  }
  const create = useMutation(
    trpc.vault.poolDepositAddress.mutationOptions({
      onSuccess: (deposit, variables) => {
        // the address goes under the pick it was asked for, and on screen only while that pick is still the one shown;
        // "fresh" is how it was asked for, not part of the pick (John, Nov 5 2026: "Use a new address" showed nothing,
        // the answer sat under a key with fresh in it that nothing reads)
        const { fresh: _fresh, ...asked } = variables
        const key = trpc.vault.openPoolDeposit.queryKey(asked)
        if (JSON.stringify(asked) === JSON.stringify(windowInputRef.current)) show(deposit, key)
        else queryClient.setQueryData(key, deposit)
        void queryClient.invalidateQueries({ queryKey: trpc.vault.deposits.queryKey() })
      },
      onError: (err) => setError(errorMessage(err)),
    }),
  )
  createRef.current = create
  // a waiting address under this pick's key that is another pick's (a cached answer gone stale) is read again once, then replaced
  const rereadKey = useRef<string | null>(null)
  useEffect(() => {
    if (activeId !== null || create.isPending || create.isError || !ready) return
    if (existing.data && existing.data.status === 'awaitingFunds') {
      if (fits(existing.data)) show(existing.data)
      else if (rereadKey.current !== JSON.stringify(openKey)) {
        rereadKey.current = JSON.stringify(openKey)
        void queryClient.invalidateQueries({ queryKey: openKey })
      } else create.mutate(windowInput)
    } else if (existing.isSuccess) create.mutate(windowInput)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, existing.data, existing.dataUpdatedAt, existing.isSuccess, asset, windowNetwork, ready, place])
  const active = useQuery({
    ...trpc.vault.deposit.queryOptions({ id: activeId ?? '' }),
    enabled: activeId !== null,
    refetchInterval: (query) => (query.state.data && POOL_TERMINAL.has(query.state.data.status) ? false : 2_000),
  })
  // opened on a deposit in progress (the bar's View): the pickers follow that deposit's coin, chain and place, not the defaults
  const [followed, setFollowed] = useState(initialId === null)
  useEffect(() => {
    const d = active.data
    if (followed || initialId === null || !d || d.id !== initialId) return
    setFollowed(true)
    if (d.platform === 'vault' || d.anyAsset) {
      setToken('token')
      setNetwork('solana')
      setToVault(true)
      setViewExchange(null)
      return
    }
    const coin = (DEPOSIT_TOKENS as readonly string[]).includes(d.tokenSymbol ?? '') ? (d.tokenSymbol as DepositToken) : null
    if (!coin) return
    setToken(coin)
    setNetwork(d.network as Network)
    setToVault(false)
    setViewExchange(isExchangeId(d.platform) ? d.platform : null)
  }, [active.data, initialId, followed])
  // the deposit on screen stopped being the pick's own (the place moved under it, Nov 5 2026: Kraken's USDC on Ethereum
  // showed nothing for good): it goes, and the pick's own is read or made. A View's deposit waits for the pickers first.
  useEffect(() => {
    if (activeId === null || !active.data || active.data.id !== activeId || !followed) return
    if (!fits(active.data)) setActiveId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, active.data, followed, token, network, place, asset])
  // the deposit on screen is the pick's own; another pick's (a View just opened, before the pickers followed it) stays off
  const deposit = active.data && fits(active.data) ? active.data : undefined
  // switching keeps the last card on screen, dimmed, until the next address is in: nothing blanks or jumps (John, Oct 31 2026)
  const [switching, setSwitching] = useState(false)
  const lastShown = useRef<VaultDepositView | undefined>(undefined)
  if (deposit) lastShown.current = deposit
  const shown = deposit ?? (switching ? lastShown.current : undefined)
  const stale = !deposit && shown !== undefined
  // the chips only while the address waits for money (John, Nov 1 2026: "why is it letting me click dropdown" on a finished deposit)
  const awaiting = !shown || shown.status === 'awaitingFunds'
  useEffect(() => {
    if (deposit) setSwitching(false)
  }, [deposit])
  useEffect(() => {
    if (!deposit) return
    void queryClient.invalidateQueries({ queryKey: trpc.vault.deposits.queryKey() })
    if (deposit.status === 'credited') void invalidatePortfolio()
    // a finished deposit is no longer the one to reopen with
    if (POOL_TERMINAL.has(deposit.status)) queryClient.setQueryData(openKey, null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit?.status])
  const waiting = useHeld(!deposit, 300)
  const over = !!deposit && POOL_TERMINAL.has(deposit.status)
  useEffect(() => {
    setFooter(
      over ? (
        <div className="flex gap-3">
          <button type="button" className="btn btn-secondary flex-1" disabled={create.isPending} onClick={() => { setActiveId(null); create.mutate(windowInputRef.current) }}>
            New deposit
          </button>
          <button type="button" className="btn btn-primary flex-1" onClick={onClose}>Done</button>
        </div>
      ) : undefined,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over, create.isPending])
  const symbol = (deposit?.tokenSymbol ?? 'SOL').toUpperCase()
  // the number alone: the chip beside it carries the ticker and the logo
  const amount = deposit?.sonarAmount && deposit.tokenDecimals !== null ? formatTokenAmount(Number(deposit.sonarAmount) / 10 ** deposit.tokenDecimals, symbol, 6) : null
  const finished = deposit?.status === 'credited'
  // the exchange's usual crediting time counting down from when it listed the deposit (John, Nov 1 2026)
  const countdown = useCountdown(deposit?.seenAt, deposit?.etaSeconds, deposit?.status === 'processing')
  // the place the record says the money is going, not the dialog's current pick: a window opened under one exchange can
  // forward to another, and the bar's chip reads the record (John, Nov 1 2026: the chip said Coinbase for a MEXC deposit)
  const recordPlace = deposit && isExchangeId(deposit.platform) ? CUSTODY_LABELS[deposit.platform] : CUSTODY_LABELS[custody]
  const left = countdownText(countdown)
  const chip = deposit && deposit.status !== 'awaitingFunds' ? <AssetChip symbol={symbol} imageUrl={tokenLogo(deposit.tokenAddress, deposit.tokenImageUrl ?? undefined)} /> : null
  // nothing is said while the address only waits: a loader before anything was sent reads as progress that is not there (John, Oct 31 2026)
  const status = !deposit || deposit.status === 'awaitingFunds' ? null : deposit.status === 'processing' ? (
      <span className="flex flex-wrap items-center justify-center gap-1.5">
        {amount ?? 'Received'} {chip}{' '}
        {deposit.sonarStatus === 'sweeping'
          ? 'arrived, moving into your balance…'
          : deposit.sonarStatus === 'arrived'
            ? 'arrived, getting ready to move it…'
          : deposit.sonarStatus === 'forwarding'
            ? `arrived, moving to ${recordPlace}…`
            : exchange
              ? `on its way, ${recordPlace} is confirming it…${left ? ` ${left}` : ''}`
              : 'on its way…'}
      </span>
    ) : deposit.status === 'credited' ? (
      <span className="flex flex-wrap items-center justify-center gap-1.5">
        Done. {amount ?? ''} {chip} arrived.{' '}
        {deposit.transactionHash ? (
          <a className="text-accent" href={txExplorerUrl(deposit.network, deposit.transactionHash)} target="_blank" rel="noreferrer">
            View txn <SquareArrowOutUpRight className="inline size-3 align-[-1px]" />
          </a>
        ) : null}
      </span>
    ) : (
      <span className="text-negative">{deposit.error ?? 'This deposit did not go through.'}</span>
    )
  // the chains, in the header after the title, while the address waits (John, Nov 4 2026)
  useEffect(() => {
    setPicker(
      awaiting && exchange ? (
        <PlatformGroup ariaLabel="Network" value={networkPlace(network)} options={DEPOSIT_NETWORKS.map((n) => ({ id: networkPlace(n), label: NETWORK_LABELS[n] }))} onChange={pickNetwork} />
      ) : undefined,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaiting, exchange, network, token])
  useEffect(() => () => setPicker(undefined), [setPicker])
  const qrLink = (
    <button type="button" className="underline-offset-2 hover:underline" onClick={qr.toggle}>
      {qr.show ? 'Hide QR code' : 'Show QR code'}
    </button>
  )
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* what is coming in and where it lands (John, Nov 4 2026): the token's chips on the left, the places on the right */}
      {awaiting && exchange ? (
        <div className="grid grid-cols-2 gap-3 text-sm">
          {/* each caption sits with its own side, the pick's name beside it (John, Nov 4 2026: "BNB To" met in the middle) */}
          <div className="grid justify-items-start gap-1.5">
            <span className="flex items-baseline gap-2">
              <span className="text-muted">Token</span>
              <span className="truncate font-medium">{token === 'token' ? 'Any token on Solana' : `${token} on ${NETWORK_LABELS[network]}`}</span>
            </span>
            <PlatformGroup ariaLabel="Token" value={token} options={tokenOptions} onChange={pickToken} />
          </div>
          <div className="grid justify-items-end gap-1.5">
            <span className="flex items-baseline gap-2">
              <span className="text-muted">To</span>
              <span className="truncate font-medium">{placeLabel(place)}</span>
            </span>
            <PlatformGroup ariaLabel="Destination" value={place} options={placeOptions} onChange={pickPlace} disabled={setCustody.isPending} />
          </div>
        </div>
      ) : null}
      {!shown ? (
        // the card's own shape, so nothing jumps; the shimmer only starts when the wait is real, and a pick no place
        // takes keeps the shape with nothing loading
        <DepositPlaceholder labels={POOL_STEPS} shimmer={waiting && !unsupported} />
      ) : (
        <div className={`flex min-w-0 flex-col gap-4 transition-opacity ${stale ? 'pointer-events-none opacity-40' : ''}`} aria-busy={stale}>
          {shown.status === 'awaitingFunds' ? <AddressCard address={shown.depositAddress} qr={qr} /> : null}
          {!exchange && shown.status === 'awaitingFunds' ? <p className="text-xs text-muted">Minimum $1. Anything less waits at the address until more arrives.</p> : null}
          <FlowProgress
            flowKey={shown.id}
            labels={POOL_STEPS}
            step={poolStep(shown.status)}
            done={shown.status === 'credited'}
            failed={shown.status === 'failed' || shown.status === 'cancelled'}
            idle={shown.status === 'awaitingFunds'}
            fees={shown.status === 'credited' ? shown.fees : undefined}
            status={stale ? null : status}
          />
        </div>
      )}
      {error ? <p className="text-sm text-negative">{error}</p> : null}
      {awaiting ? (
        // the links keep their place while the next address loads, so the card above neither drops nor rises on a
        // switch (John, Nov 4 2026: "not jump up/down size change when switching token"); the QR code opens from here
        <div className={`flex items-center gap-2 text-xs text-muted ${shown?.status === 'awaitingFunds' && !stale ? '' : 'invisible'}`}>
          {exchange && !direct ? (
            <>
              <button
                type="button"
                className="underline-offset-2 hover:underline"
                disabled={create.isPending || stale || !shown}
                onClick={() => {
                  // another address while this one still waits: several deposits at once (John, Oct 31 2026)
                  restart()
                  create.mutate({ ...windowInput, fresh: true })
                }}
              >
                Use a new address for another deposit
              </button>
              <span aria-hidden="true">|</span>
            </>
          ) : null}
          {qrLink}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The address card's shape while there is no address to show: a box the size of the address field (an invisible
 * address of a Solana address's length, so it wraps where a real one would) over the idle stepper. The dialog keeps
 * its size while an address loads or the token switches (John, Nov 4 2026).
 */
function DepositPlaceholder({ labels, shimmer }: { labels: readonly string[]; shimmer: boolean }) {
  return (
    <div className="flex min-w-0 flex-col gap-4" aria-busy={shimmer}>
      <div className={`control flex w-full items-center px-3 py-3.5 ${shimmer ? 'skeleton' : ''}`}>
        <code className="invisible min-w-0 break-all font-mono text-sm leading-snug" aria-hidden="true">
          {'0'.repeat(44)}
        </code>
      </div>
      <FlowProgress labels={labels} step={0} idle status={null} />
    </div>
  )
}

function AddressCard({ address, qr }: { address: string; qr: QrControl }) {
  // The QR stays folded until asked for (John, Oct 29 2026): the address is what most people copy.
  // Its button sits in the header; the card tells the header which address to encode while it is on screen.
  const { setAddress } = qr
  useEffect(() => {
    setAddress(address)
    return () => setAddress(null)
  }, [address, setAddress])
  return (
    <div className="flex flex-col items-center gap-4">
      <AddressField address={address} size="lg" />
      {qr.show ? (
        <div className="rounded-card bg-white p-3">
          <QRCodeSVG value={address} size={168} />
        </div>
      ) : null}
    </div>
  )
}

function PrivateDeposit({ wallet, onClose, setFooter, qr }: { wallet: MeWallet; onClose: () => void; setFooter: (footer: React.ReactNode) => void; qr: QrControl }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const config = useQuery(trpc.privateDeposit.config.queryOptions())
  const list = useQuery({ ...trpc.privateDeposit.list.queryOptions(), refetchInterval: 10_000 })
  const [activeId, setActiveId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const create = useMutation(
    trpc.privateDeposit.create.mutationOptions({
      onSuccess: (deposit) => {
        setActiveId(deposit.id)
        void queryClient.invalidateQueries({ queryKey: trpc.privateDeposit.list.queryKey() })
      },
      onError: (err) => setError(errorMessage(err)),
    }),
  )
  // Opening the dialog shows what still needs attention for this wallet (in
  // flight, or overdue) before opening a fresh one-time address. A finished
  // deposit is not shown again (John, Oct 29 2026).
  useEffect(() => {
    if (activeId !== null || !list.isSuccess || create.isPending || create.isError) return
    const mine = list.data.filter((d) => d.walletAddress === wallet.solanaAddress)
    const inFlight = mine.find((d) => d.status === 'funded' || d.status === 'sweepSent' || d.status === 'relayPending')
    const needsALook = mine.find((d) => d.status === 'failed' || d.status === 'stalled')
    const waiting = mine.find((d) => d.status === 'awaitingFunds')
    const pick = inFlight ?? needsALook ?? waiting
    if (pick) setActiveId(pick.id)
    else create.mutate({ walletId: wallet.id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, list.isSuccess])
  const active = useQuery({
    ...trpc.privateDeposit.get.queryOptions({ id: activeId ?? '' }),
    enabled: activeId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      // an overdue fill keeps being checked once a minute server-side
      return status === 'completed' || status === 'failed' ? false : status === 'stalled' ? 30_000 : 2_000
    },
  })
  const deposit = active.data
  useEffect(() => {
    if (!deposit) return
    // keep the history list in step with the stepper
    void queryClient.invalidateQueries({ queryKey: trpc.privateDeposit.list.queryKey() })
    if (deposit.status === 'completed') {
      markPortfolioFresh()
      void queryClient.invalidateQueries({ queryKey: trpc.portfolio.get.queryKey() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit?.status])
  // a token cycle finishes when Sonar credits the vault deposit
  const vaultDeposit = useQuery({
    ...trpc.vault.deposit.queryOptions({ id: deposit?.vaultDepositId ?? '' }),
    enabled: !!deposit?.vaultDepositId,
    refetchInterval: (query) => (query.state.data && ['credited', 'failed', 'cancelled'].includes(query.state.data.status) ? false : 5_000),
  })
  const credited = vaultDeposit.data?.status === 'credited'
  useEffect(() => {
    if (credited) {
      markPortfolioFresh()
      void queryClient.invalidateQueries({ queryKey: trpc.portfolio.get.queryKey() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credited])
  const cfg = config.data
  const minSol = cfg ? Number(cfg.sol.minRaw) / 10 ** cfg.sol.decimals : undefined
  const minStable = cfg ? Number(cfg.stable.minRaw) / 10 ** cfg.stable.decimals : undefined
  const over = !!deposit && TERMINAL.has(deposit.status)
  useEffect(() => {
    setFooter(
      over ? (
        <div className="flex gap-3">
          <button type="button" className="btn btn-secondary flex-1" disabled={create.isPending} onClick={() => create.mutate({ walletId: wallet.id })}>
            New deposit
          </button>
          <button type="button" className="btn btn-primary flex-1" onClick={onClose}>Done</button>
        </div>
      ) : undefined,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over, create.isPending])
  const tokenCycle = deposit?.asset.kind === 'token'
  // a token's "Complete" waits for Sonar's credit; its hand-over is step 2 while Sonar sweeps
  const step = !deposit ? -1 : tokenCycle && deposit.status === 'completed' ? (credited ? 3 : 2) : stepIndex(deposit.status)
  const steps = tokenCycle ? TOKEN_STEPS : STEPS
  const finished = deposit?.status === 'completed' && (!tokenCycle || credited)

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {!deposit ? (
        <div className="flex flex-col items-center gap-4">
          <div className="skeleton h-12 w-full" />
          <div className="skeleton h-10 w-36" />
        </div>
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          {deposit.status === 'awaitingFunds' ? <AddressCard address={deposit.depositAddress} qr={qr} /> : null}
          {deposit.status === 'awaitingFunds' ? (
            <button type="button" className="self-start text-xs text-muted underline-offset-2 hover:underline" onClick={qr.toggle}>
              {qr.show ? 'Hide QR code' : 'Show QR code'}
            </button>
          ) : null}
          <FlowProgress
            flowKey={deposit?.id}
            labels={steps.map((label) => {
              const parts = deposit.sweeps.length
              const sent = deposit.sweeps.filter((c) => c.signature).length
              return label === 'Routing' && parts > 1 ? `Routing ${Math.min(sent, parts)}/${parts}` : label
            })}
            step={step}
            done={finished}
            failed={deposit.status === 'failed'}
            idle={deposit.status === 'awaitingFunds'}
            fees={finished ? deposit.fees : undefined}
            status={deposit.status === 'awaitingFunds' ? null : <DepositStatusLine deposit={deposit} credited={credited} />}
          />
          {deposit.status === 'awaitingFunds' ? (
            <p className="text-xs text-muted">
              Minimums {minSol ?? '…'} SOL, {minStable ?? '…'} USDC or USDT.
            </p>
          ) : null}
        </div>
      )}
      {create.isPending ? <Spinner className="mx-auto" /> : null}
      {error ? <p className="text-sm text-negative">{error}</p> : null}
    </div>
  )
}

function DepositStatusLine({ deposit, credited }: { deposit: PrivateDepositView; credited: boolean }) {
  const asset = deposit.asset
  const fmt = (raw: string | null | undefined) => amountOf(raw, asset)
  const parts = deposit.sweeps.length
  const sent = deposit.sweeps.filter((c) => c.signature)
  const paid = deposit.sweeps.filter((c) => c.completedAt).length
  const latest = sent[sent.length - 1]
  if (asset.kind === 'token' && deposit.status !== 'awaitingFunds') {
    const chip = <AssetChip symbol={asset.symbol.toUpperCase()} imageUrl={tokenLogo(asset.mint ?? undefined, asset.imageUrl ?? undefined)} />
    switch (deposit.status) {
      case 'funded':
      case 'sweepSent':
        return (
          <span className="flex flex-wrap items-center justify-center gap-1.5">
            {numberOf(deposit.fundedLamports, asset)} {chip} received, on its way…{' '}
            {deposit.forward?.signature ? (
              <a className="text-accent" href={`https://solscan.io/tx/${deposit.forward.signature}`} target="_blank" rel="noreferrer">
                View transfer
              </a>
            ) : null}
          </span>
        )
      case 'completed':
        return (
          <span className="flex flex-wrap items-center justify-center gap-1.5">
            Done. {numberOf(deposit.fundedLamports, asset)} {chip} {credited ? 'arrived.' : 'arrives shortly.'}{' '}
            {deposit.forward?.signature ? (
              <a className="text-accent" href={`https://solscan.io/tx/${deposit.forward.signature}`} target="_blank" rel="noreferrer">
                View transfer
              </a>
            ) : null}
          </span>
        )
      case 'failed':
        return (
          <span>
            Not through yet, retrying…
          </span>
        )
      default:
        return null
    }
  }
  switch (deposit.status) {
    case 'funded':
      return (
        <span>
          {fmt(deposit.fundedLamports)} received, on its way…
        </span>
      )
    case 'sweepSent':
      return (
        <span>
          Routing {fmt(deposit.fundedLamports)}{parts > 1 ? ` in ${parts} parts` : ''}…{' '}
          {latest?.signature ? (
            <a className="text-accent" href={`https://solscan.io/tx/${latest.signature}`} target="_blank" rel="noreferrer">
              View deposit <SquareArrowOutUpRight className="inline size-3 align-[-1px]" />
            </a>
          ) : null}
        </span>
      )
    case 'relayPending':
      return (
        <span>
          Arriving{parts > 1 ? `, ${paid} of ${parts} parts` : ''}…
        </span>
      )
    case 'completed':
      return (
        <span>
          Done. {fmt(deposit.fill?.receivedLamports ?? deposit.sweep?.expectedOutLamports ?? null)} arrived.{' '}
          {deposit.fill?.txHash ? (
            <a className="text-accent" href={`https://solscan.io/tx/${deposit.fill.txHash}`} target="_blank" rel="noreferrer">
              View txn <SquareArrowOutUpRight className="inline size-3 align-[-1px]" />
            </a>
          ) : null}
        </span>
      )
    case 'failed':
      return (
        <span>
          Not through yet, retrying…
        </span>
      )
    case 'stalled':
      return (
        <span className="text-warning">
          Taking longer than usual{parts > 1 ? `, ${paid} of ${parts} parts arrived` : ''}…
        </span>
      )
  }
}
